import {
  ProcureVendor,
  updateProcureVendor as updateProcureVendorMutation,
  deleteProcureVendor as deleteProcureVendorMutation,
  getProcureVendorsWithFilters,
  getProcureVendorById,
  PermissionsEnum,
} from 'vendor-marketplace-shared-library';
import { Avatar, Grid, IconButton, Typography } from '@mui/material';
import { formatPhoneNumber } from '../../../utilities/formatters';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import ProcureVendorForm from '../../../components/vendors/ProcureVendorForm';
import Modal from '../../../components/common/Modal';
import { ProcureVendorFormInput } from '../../../types/vendors';
import { AddressInput } from '../../../types/google-maps';
import { useMutation } from '@apollo/client';
import { PROCURE_VENDOR_UPDATE_ERROR } from '../../../error-messages';
import useAlert from '../../../hooks/useAlert';
import useAuth from '../../../hooks/useAuth';

type ProcureVendorDetailsModuleProps = {
  vendor: ProcureVendor;
};

export default function ProcureVendorDetailsModule(props: ProcureVendorDetailsModuleProps) {
  const alert = useAlert();
  const navigate = useNavigate();
  const { validateUserPermissions } = useAuth();
  const [openEditModal, setOpenEditModal] = useState(false);
  const [procureVendorFormInput, setProcureVendorFormInput] = useState<ProcureVendorFormInput>({
    id: props.vendor.id,
    name: props.vendor.name,
    phone: props.vendor.phone || '',
    email: props.vendor.email || '',
    issueTypeIds:
      (props.vendor.issueTypes && props.vendor.issueTypes.map(issueType => issueType.id)) || [],
    propertyIds: props.vendor.properties?.map(property => property.id) || [],
    procureVendorContacts:
      props.vendor.procureVendorContacts?.map(contact => ({
        id: contact.id || '',
        fullName: contact.fullName || '',
        role: contact.role || '',
        phone: contact.phone || '',
        email: contact.email || '',
        notes: contact.notes || '',
      })) || [],
  });

  const [address, setAddress] = useState<AddressInput>({
    address1: props.vendor.address1 || '',
    address2: props.vendor.address2 || '',
    city: props.vendor.city || '',
    state: props.vendor.state || '',
    zipcode: props.vendor.zipcode || '',
  });

  const [updateProcureVendor] = useMutation(updateProcureVendorMutation, {
    variables: {
      id: props.vendor.id,
      input: {
        name: procureVendorFormInput.name,
        phone: procureVendorFormInput.phone,
        email: procureVendorFormInput.email,
        issueTypeIds: procureVendorFormInput.issueTypeIds,
        propertyIds: procureVendorFormInput.propertyIds,
        procureVendorContacts: procureVendorFormInput.procureVendorContacts,
        ...address,
      },
    },
    refetchQueries: [getProcureVendorsWithFilters, getProcureVendorById],
    onCompleted: () => {
      setOpenEditModal(false);
    },
    onError: () => {
      alert.setAlert({ message: PROCURE_VENDOR_UPDATE_ERROR });
    },
  });

  return (
    <Grid container rowGap={2}>
      <Grid item container sx={{ justifyContent: 'space-between' }}>
        <Grid item>
          <Grid item container columnGap={2}>
            {/*Back Button*/}
            <Grid item>
              <IconButton onClick={() => navigate('/vendors')}>
                <ArrowBackIcon />
              </IconButton>
            </Grid>
            {/*Name and role*/}
            <Grid item>
              <Grid item container direction={'row'} sx={styles.nameContainer} xs={12}>
                <Grid item>
                  <Avatar />
                </Grid>
                <Grid item container direction={'column'}>
                  <Grid item>
                    <Typography variant={'h6'}>{props.vendor.name}</Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant={'caption'} color={'secondary.main'}>
                      {props.vendor.address1}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        {validateUserPermissions([PermissionsEnum.PROCURE_VENDOR_UPDATE]) && (
          <Grid item>
            <IconButton onClick={() => setOpenEditModal(true)}>
              <EditIcon />
            </IconButton>
          </Grid>
        )}
      </Grid>
      {/*Contact info*/}
      <Grid item container direction={'row'} spacing={1} xs={12}>
        {/*Phone*/}
        <Grid item>
          <Grid container spacing={1}>
            <Grid item>
              <PhoneIcon />
            </Grid>
            <Grid item>
              <Typography>
                {formatPhoneNumber(props.vendor.phone || '') ?? 'No phone number set'}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <Typography>|</Typography>
        </Grid>
        {/*Email*/}
        <Grid item>
          <Grid container spacing={1}>
            <Grid item>
              <EmailIcon />
            </Grid>
            <Grid item>
              <Typography>{props.vendor.email ?? 'No email set'}</Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {/*Edit Modal*/}
      <Modal title={'Edit Vendor'} open={openEditModal} setOpen={setOpenEditModal}>
        <ProcureVendorForm
          procureVendorFormInput={procureVendorFormInput}
          setProcureVendorFormInput={setProcureVendorFormInput}
          address={address}
          setAddress={setAddress}
          onSubmit={updateProcureVendor}
        />
      </Modal>
    </Grid>
  );
}

const styles = {
  nameContainer: {
    flexWrap: 'nowrap',
    columnGap: 1,
  },
};
